/**
 * eReg Folders Page
 *
 * Navigate folder structure, view documents, and manage them:
 * view/download, rename, new version, request signatures, archive,
 * soft-delete and restore. Signature state renders as chips
 * (sign-the-version model — see docs/ereg-source-of-truth-plan.md §4.2).
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Folder as FolderIcon,
  FolderOpen,
  FolderInput,
  FileText,
  ChevronRight,
  Plus,
  MoreVertical,
  X,
  Copy,
  Eye,
  Download,
  PenTool,
  Pencil,
  FilePlus2,
  Archive,
  ArchiveRestore,
  RotateCcw,
  Trash2,
  AlertCircle,
  ArrowLeft,
  Pin,
  RefreshCw,
  History,
  Loader2,
  CheckCircle,
  Tag,
} from 'lucide-react';
import { useEreg } from '../contexts/EregContext';
import { useAuth } from '../../contexts/AuthContext';
import * as eregApi from '../lib/api';
import { fieldCls, primaryBtnCls, secondaryBtnCls, dangerBtnCls, useToast } from '../../ui';
import { KeyDocumentsPanel, PinKeyDocumentModal } from '../components/KeyDocumentsPanel';
import { getDocumentSignatureState } from '../types';
import { formatDateOnly } from '../lib/dates';
import type {
  Folder, Document, DocumentType, FolderTemplate, User,
  DocumentVersionsResponse, CancelledSignatureRequest, UploadVersionResponse,
} from '../types';

// Signature chip for a document row. P-10: no longer inert — a pending chip
// with YOUR assignment links to the queue, and "Newer version unsigned" opens
// the version history (the artifact it references is finally viewable).
function SignatureChip({ doc, mine, onSignNow, onShowVersions }: {
  doc: Document;
  mine: boolean;
  onSignNow: () => void;
  onShowVersions: () => void;
}) {
  const state = getDocumentSignatureState(doc);
  if (state === 'none') return null;

  const styles: Record<string, string> = {
    signed: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    newer_version_unsigned: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  };
  const labels: Record<string, string> = {
    signed: 'Signed',
    pending: mine ? 'Signature pending — sign now' : 'Signature pending',
    newer_version_unsigned: 'Newer version unsigned',
  };

  const chip = (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${styles[state]}`}>
      <PenTool size={10} />
      {labels[state]}
    </span>
  );

  if (state === 'pending' && mine) {
    return (
      <button
        type="button"
        onClick={onSignNow}
        className="rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500"
        title="This document is waiting on YOUR signature — open the signature queue"
      >
        {chip}
      </button>
    );
  }
  if (state === 'newer_version_unsigned') {
    return (
      <button
        type="button"
        onClick={onShowVersions}
        className="rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500"
        title="A prior version carries signatures — open the version history"
      >
        {chip}
      </button>
    );
  }
  return chip;
}

/** Why "Request signature" is unavailable for this document (null = allowed). */
function signatureRequestBlockReason(doc: Document): string | null {
  if (doc.status === 'archived') return 'Archived documents can’t be routed for signature — restore it first.';
  if (doc.mime_type && doc.mime_type !== 'application/pdf') return 'Only PDFs can be routed for signature — upload a PDF version first.';
  if (doc.signed_at) return 'This version is fully signed — upload a new version to request signatures again.';
  if ((doc.signature_requests || []).some((r) => r.status === 'pending')) return 'A signature request is already pending for this document.';
  return null;
}

export function Folders() {
  const { folders, foldersLoading, currentFolder, setCurrentFolder, currentProtocol, siteMode, refreshFolders, isAdmin, documentTypes } = useEreg();
  const { currentSite } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Deep-link return context (plan §3.6) — handed over via router state by
  // StudyScope's index redirect (/ereg/studies/:id?return_to=…).
  const returnTo = (location.state as any)?.returnTo as string | undefined;
  const returnLabel = ((location.state as any)?.returnLabel as string | undefined) || 'previous page';

  // Canonical upload URL for this scope (study / site zone / legacy fallback).
  const uploadPath = !siteMode && currentProtocol
    ? `/ereg/studies/${currentProtocol.id}/upload`
    : siteMode
      ? '/ereg/site/upload'
      : '/ereg/upload';

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState<FolderTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Document action states
  const [menuDocId, setMenuDocId] = useState<string | null>(null);
  const [renameDoc, setRenameDoc] = useState<Document | null>(null);
  const [changeTypeDoc, setChangeTypeDoc] = useState<Document | null>(null);
  const [versionDoc, setVersionDoc] = useState<Document | null>(null);
  const [requestSigDoc, setRequestSigDoc] = useState<Document | null>(null);
  const [deleteDoc, setDeleteDoc] = useState<Document | null>(null);
  const [pinDoc, setPinDoc] = useState<Document | null>(null);
  const [historyDoc, setHistoryDoc] = useState<Document | null>(null);
  const [moveDoc, setMoveDoc] = useState<Document | null>(null);
  const [reRequest, setReRequest] = useState<{ document: Document; cancelled: CancelledSignatureRequest } | null>(null);
  const [renameFolderTarget, setRenameFolderTarget] = useState<Folder | null>(null);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<Folder | null>(null);
  const [keyDocRefresh, setKeyDocRefresh] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  // P-10: which documents carry a pending signature assignment for THIS user
  // — powers "sign now" from the row. Fail-soft; the chip just stays inert.
  const [myPendingDocIds, setMyPendingDocIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    let cancelled = false;
    eregApi.getSignatureQueue()
      .then((items) => {
        if (cancelled) return;
        setMyPendingDocIds(new Set(
          items
            .map((item) => item.signature_request?.document?.id)
            .filter((id): id is string => Boolean(id))
        ));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const loadDocuments = useCallback(async () => {
    if (!currentFolder) {
      setDocuments([]);
      return;
    }
    setDocumentsLoading(true);
    try {
      const docs = await eregApi.getFolderDocuments(currentFolder.id, { include_archived: showArchived });
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  }, [currentFolder, showArchived]);

  // Load documents when folder is selected or the archived toggle changes
  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // ?folder_id= deep link (search results, StudyScope index pass-through):
  // once the folder list is in, select the target folder and drop the param
  // so normal navigation isn't sticky.
  const deepLinkFolderId = searchParams.get('folder_id');
  useEffect(() => {
    if (!deepLinkFolderId || foldersLoading) return;
    if (folders.length === 0) return; // wait for a real load; param drops below once loaded
    const target = folders.find((f) => f.id === deepLinkFolderId);
    if (target) setCurrentFolder(target);
    const next = new URLSearchParams(searchParams);
    next.delete('folder_id');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkFolderId, folders, foldersLoading]);

  // Close the row menu on outside click
  useEffect(() => {
    if (!menuDocId) return;
    const onMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuDocId(null);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [menuDocId]);

  // Load templates when template modal opens — scoped to the STUDY's site so
  // the picker never offers a template the apply route would reject.
  useEffect(() => {
    if (showTemplateModal) {
      const targetSiteId = (!siteMode && currentProtocol?.site_id) || currentSite?.id;
      setTemplatesLoading(true);
      eregApi.getFolderTemplates({
        is_site_template: siteMode,
        site_id: targetSiteId,
      })
        .then(setTemplates)
        .catch(console.error)
        .finally(() => setTemplatesLoading(false));
    }
  }, [showTemplateModal, siteMode, currentProtocol?.site_id, currentSite?.id]);

  // Find root folders (no parent)
  const rootFolders = folders.filter(f => !f.parent_folder_id);

  // Find child folders of current folder
  const childFolders = currentFolder
    ? folders.filter(f => f.parent_folder_id === currentFolder.id)
    : [];

  // Build breadcrumb path
  const buildBreadcrumb = () => {
    if (!currentFolder) return [];
    const parts = currentFolder.path.split('/').filter(Boolean);
    const breadcrumb: { name: string; folder: Folder | null }[] = [];

    let currentPath = '';
    for (const part of parts) {
      currentPath += `/${part}`;
      const folder = folders.find(f => f.path === currentPath);
      breadcrumb.push({ name: part, folder: folder || null });
    }

    return breadcrumb;
  };

  const breadcrumb = buildBreadcrumb();

  const handleFolderClick = (folder: Folder) => {
    setCurrentFolder(folder);
  };

  const handleBreadcrumbClick = (folder: Folder | null) => {
    setCurrentFolder(folder);
  };

  const handleCreateFolder = async (name: string) => {
    if (!currentSite) return;

    // The folder belongs to the STUDY's site, which can differ from the
    // global site selector for multi-site users — the backend now rejects a
    // protocol/site mismatch.
    const targetSiteId = (!siteMode && currentProtocol?.site_id) || currentSite.id;

    try {
      await eregApi.createFolder({
        protocol_id: siteMode ? undefined : currentProtocol?.id,
        site_id: targetSiteId,
        parent_folder_id: currentFolder?.id,
        name,
      });
      await refreshFolders(currentProtocol?.id);
      setShowCreateModal(false);
    } catch (error: any) {
      console.error('Failed to create folder:', error);
      // Rethrow with the server's reason so the modal (which sits on top of
      // the page banner) can show it inline.
      throw new Error(error?.response?.data?.error || 'Failed to create folder');
    }
  };

  const handleApplyTemplate = async (templateId: string) => {
    if (!currentSite || !currentProtocol) return;

    try {
      await eregApi.applyFolderTemplate(currentProtocol.id, templateId);
      await refreshFolders(currentProtocol.id);
      setShowTemplateModal(false);
    } catch (error: any) {
      console.error('Failed to apply template:', error);
      throw new Error(error?.response?.data?.error || 'Failed to apply template');
    }
  };

  // ---- Document actions ----

  const openDoc = async (doc: Document, download = false) => {
    // Open the window synchronously inside the click gesture (popup blockers
    // reject window.open after an await), then point it at the presigned URL.
    const win = window.open('about:blank', '_blank');
    if (win) win.opener = null;
    try {
      const { url } = download
        ? await eregApi.getDocumentDownloadUrl(doc.id)
        : await eregApi.getDocumentViewUrl(doc.id);
      if (win) {
        win.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener');
      }
    } catch (error) {
      if (win) win.close();
      console.error('Failed to open document:', error);
      setActionError('Failed to open document');
    }
  };

  const handleArchiveToggle = async (doc: Document) => {
    try {
      await eregApi.updateDocument(doc.id, { status: doc.status === 'archived' ? 'active' : 'archived' });
      await loadDocuments();
    } catch (error: any) {
      console.error('Failed to update document:', error);
      // Slot guard (§3.2): a pinned key document can't be archived.
      setActionError(error?.response?.data?.code === 'EREG_KEY_DOCUMENT_IN_USE'
        ? error.response.data.error
        : 'Failed to update document');
    }
  };

  const handleReextract = async (doc: Document) => {
    try {
      await eregApi.reextractDocumentText(doc.id);
      toast('Text extraction queued — the current version will re-extract shortly');
    } catch (error) {
      console.error('Failed to queue re-extraction:', error);
      toast('Failed to queue text extraction', { type: 'error' });
    }
  };

  const handleRestore = async (doc: Document) => {
    try {
      await eregApi.restoreDocument(doc.id);
      await loadDocuments();
    } catch (error) {
      console.error('Failed to restore document:', error);
      setActionError('Failed to restore document');
    }
  };

  if (!currentProtocol && !siteMode) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center dark:bg-gray-800 dark:border-gray-700">
          <FolderIcon size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No Study Selected</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Select a study or site documents from the header dropdown to view folders.
          </p>
          <Link
            to="/ereg/dashboard"
            className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
          >
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {returnTo && (
        <Link
          to={returnTo}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {returnLabel}
        </Link>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">
            {siteMode ? 'Site Documents' : currentProtocol?.protocol_number}
          </h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            {siteMode ? 'CVs, licenses, and site certifications' : currentProtocol?.protocol_title || 'Document Repository'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Apply Template Button - only show if protocol has no folders */}
          {!siteMode && rootFolders.length === 0 && (
            <button
              onClick={() => setShowTemplateModal(true)}
              className={secondaryBtnCls}
            >
              <Copy size={16} />
              <span>Apply Template</span>
            </button>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className={primaryBtnCls}
          >
            <Plus size={16} />
            <span>New Folder</span>
          </button>
        </div>
      </div>

      {/* Action error */}
      {actionError && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <span className="flex items-center gap-2"><AlertCircle size={16} />{actionError}</span>
          <button onClick={() => setActionError(null)} className="rounded p-0.5 hover:bg-red-100 dark:hover:bg-red-900/40" aria-label="Dismiss error">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Key-document slots + "used by" chips (§3.2, §3.7) */}
      {!siteMode && currentProtocol && (
        <KeyDocumentsPanel
          protocolId={currentProtocol.id}
          refreshToken={keyDocRefresh}
          onChanged={loadDocuments}
        />
      )}

      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm">
        <button
          onClick={() => handleBreadcrumbClick(null)}
          className={`px-2 py-1 rounded transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-800 ${
            !currentFolder ? 'font-medium text-primary-600 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400'
          }`}
        >
          Root
        </button>
        {breadcrumb.map((item, index) => (
          <div key={index} className="flex items-center">
            <ChevronRight size={16} className="text-gray-400 dark:text-gray-500" />
            <button
              onClick={() => item.folder && handleBreadcrumbClick(item.folder)}
              className={`px-2 py-1 rounded transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-800 ${
                index === breadcrumb.length - 1 ? 'font-medium text-primary-600 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              {item.name}
            </button>
          </div>
        ))}
      </nav>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Folders Panel */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50 flex items-center justify-between gap-2">
            <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">
              {currentFolder ? 'Subfolders' : 'Folders'}
            </h3>
            {currentFolder && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setRenameFolderTarget(currentFolder)}
                  title={`Rename "${currentFolder.name}"`}
                  aria-label={`Rename folder ${currentFolder.name}`}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteFolderTarget(currentFolder)}
                  title={`Delete "${currentFolder.name}" (must be empty)`}
                  aria-label={`Delete folder ${currentFolder.name}`}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-red-500 transition-colors duration-150 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-red-400 dark:hover:bg-red-900/20"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            )}
          </div>

          {foldersLoading ? (
            <div className="p-4 text-center">
              <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 dark:border-primary-400"></div>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {(currentFolder ? childFolders : rootFolders).length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                  {rootFolders.length === 0 && !currentFolder ? (
                    <div>
                      <p className="mb-2">No folders yet</p>
                      {!siteMode && (
                        <button
                          onClick={() => setShowTemplateModal(true)}
                          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-sm font-medium"
                        >
                          Apply a folder template
                        </button>
                      )}
                    </div>
                  ) : (
                    'No subfolders'
                  )}
                </div>
              ) : (
                (currentFolder ? childFolders : rootFolders).map((folder) => (
                  <button
                    key={folder.id}
                    onClick={() => handleFolderClick(folder)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150 text-left"
                  >
                    {currentFolder?.id === folder.id ? (
                      <FolderOpen size={20} className="text-primary-600 dark:text-primary-400 flex-shrink-0" />
                    ) : (
                      <FolderIcon size={20} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{folder.name}</div>
                      {folder.allowed_document_types && folder.allowed_document_types.length > 0 && (
                        <div className="text-xs text-gray-400 dark:text-gray-500 truncate">
                          {folder.allowed_document_types.join(', ')}
                        </div>
                      )}
                    </div>
                    <ChevronRight size={16} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Documents Panel */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 rounded-t-xl dark:border-gray-700 dark:bg-gray-800/50 flex items-center justify-between gap-2">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              Documents {currentFolder && `in ${currentFolder.name}`}
            </h3>
            {currentFolder && (
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                  />
                  Show archived &amp; deleted
                </label>
                <Link
                  to={uploadPath}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                >
                  Upload
                </Link>
              </div>
            )}
          </div>

          {!currentFolder ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <FileText size={40} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
              <p>Select a folder to view documents</p>
            </div>
          ) : documentsLoading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 dark:border-primary-400"></div>
            </div>
          ) : documents.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <FileText size={40} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
              <p>No documents in this folder</p>
              <Link
                to={uploadPath}
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium mt-2 inline-block"
              >
                Upload a document
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {documents.map((doc) => {
                const isDeleted = Boolean(doc.deleted_at);
                const isArchived = doc.status === 'archived';
                return (
                  <div
                    key={doc.id}
                    className={`relative flex items-center gap-3 px-4 py-3 transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                      isDeleted ? 'opacity-60' : ''
                    }`}
                  >
                    <FileText size={20} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => !isDeleted && openDoc(doc)}
                        className="font-medium text-gray-900 dark:text-gray-100 truncate hover:text-primary-600 dark:hover:text-primary-400 text-left block max-w-full"
                        title={doc.document_name}
                      >
                        {doc.document_name}
                      </button>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                        <span>{doc.document_type}</span>
                        <span>v{doc.version_number}</span>
                        {isDeleted ? (
                          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                            Deleted
                          </span>
                        ) : isArchived ? (
                          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300">
                            Archived
                          </span>
                        ) : null}
                        <SignatureChip
                          doc={doc}
                          mine={myPendingDocIds.has(doc.id)}
                          onSignNow={() => navigate('/ereg/signatures')}
                          onShowVersions={() => setHistoryDoc(doc)}
                        />
                        {(doc.key_document_slots || []).map((slot) => (
                          <span
                            key={slot.id}
                            className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/20 dark:text-primary-400"
                            title="Pinned key document — other tools read this slot's current version"
                          >
                            <Pin size={10} />
                            Key: {slot.role}{slot.variant_label !== 'default' ? ` (${slot.variant_label})` : ''}
                          </span>
                        ))}
                        {doc.expiration_date && (
                          <span>expires {formatDateOnly(doc.expiration_date)}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setMenuDocId(menuDocId === doc.id ? null : doc.id)}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 sm:h-8 sm:w-8"
                      aria-label={`Actions for ${doc.document_name}`}
                    >
                      <MoreVertical size={16} />
                    </button>

                    {/* Row actions menu */}
                    {menuDocId === doc.id && (
                      <div
                        ref={menuRef}
                        className="absolute right-4 top-12 z-20 w-52 rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
                      >
                        {isDeleted ? (
                          <MenuItem icon={<RotateCcw size={14} />} label="Restore" onClick={() => { setMenuDocId(null); handleRestore(doc); }} />
                        ) : (
                          <>
                            <MenuItem icon={<Eye size={14} />} label="View" onClick={() => { setMenuDocId(null); openDoc(doc); }} />
                            <MenuItem icon={<Download size={14} />} label="Download" onClick={() => { setMenuDocId(null); openDoc(doc, true); }} />
                            {myPendingDocIds.has(doc.id) && (
                              <MenuItem
                                icon={<PenTool size={14} />}
                                label="Sign now"
                                onClick={() => { setMenuDocId(null); navigate('/ereg/signatures'); }}
                              />
                            )}
                            <MenuItem
                              icon={<PenTool size={14} />}
                              label="Request signature"
                              disabledReason={signatureRequestBlockReason(doc)}
                              onClick={() => { setMenuDocId(null); setRequestSigDoc(doc); }}
                            />
                            <MenuItem icon={<History size={14} />} label="Version history" onClick={() => { setMenuDocId(null); setHistoryDoc(doc); }} />
                            <MenuItem icon={<FilePlus2 size={14} />} label="Upload new version" onClick={() => { setMenuDocId(null); setVersionDoc(doc); }} />
                            <MenuItem icon={<FolderInput size={14} />} label="Move to folder…" onClick={() => { setMenuDocId(null); setMoveDoc(doc); }} />
                            {!siteMode && currentProtocol && !isArchived && (
                              <MenuItem icon={<Pin size={14} />} label="Pin as key document" onClick={() => { setMenuDocId(null); setPinDoc(doc); }} />
                            )}
                            <MenuItem icon={<RefreshCw size={14} />} label="Re-extract text" onClick={() => { setMenuDocId(null); handleReextract(doc); }} />
                            {isAdmin && (
                              <MenuItem
                                icon={<History size={14} />}
                                label="Audit history"
                                onClick={() => { setMenuDocId(null); navigate(`/ereg/audit?document_id=${doc.id}`); }}
                              />
                            )}
                            <MenuItem icon={<Pencil size={14} />} label="Rename" onClick={() => { setMenuDocId(null); setRenameDoc(doc); }} />
                            <MenuItem icon={<Tag size={14} />} label="Change type" onClick={() => { setMenuDocId(null); setChangeTypeDoc(doc); }} />
                            <MenuItem
                              icon={isArchived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                              label={isArchived ? 'Unarchive' : 'Archive'}
                              onClick={() => { setMenuDocId(null); handleArchiveToggle(doc); }}
                            />
                            <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                            <MenuItem icon={<Trash2 size={14} />} label="Delete" danger onClick={() => { setMenuDocId(null); setDeleteDoc(doc); }} />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Create Folder Modal */}
      {showCreateModal && (
        <CreateFolderModal
          parentFolder={currentFolder}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateFolder}
        />
      )}

      {/* Apply Template Modal */}
      {showTemplateModal && (
        <ApplyTemplateModal
          templates={templates}
          loading={templatesLoading}
          onClose={() => setShowTemplateModal(false)}
          onApply={handleApplyTemplate}
        />
      )}

      {/* Rename Modal */}
      {renameDoc && (
        <RenameDocumentModal
          document={renameDoc}
          onClose={() => setRenameDoc(null)}
          onRenamed={() => { setRenameDoc(null); loadDocuments(); }}
        />
      )}

      {/* Change Document Type Modal */}
      {changeTypeDoc && (
        <ChangeDocumentTypeModal
          document={changeTypeDoc}
          documentTypes={documentTypes}
          onClose={() => setChangeTypeDoc(null)}
          onChanged={() => { setChangeTypeDoc(null); loadDocuments(); toast('Document type updated'); }}
        />
      )}

      {/* New Version Modal — E-3: when the upload cancels an open signature
          request, hand it to the re-request prompt instead of vanishing. */}
      {versionDoc && (
        <NewVersionModal
          document={versionDoc}
          onClose={() => setVersionDoc(null)}
          onUploaded={(result) => {
            const cancelled = result?.cancelled_signature_requests?.[0] || null;
            const uploadedDoc = versionDoc;
            setVersionDoc(null);
            loadDocuments();
            if (cancelled && uploadedDoc) {
              setReRequest({ document: { ...uploadedDoc, ...result }, cancelled });
            }
          }}
        />
      )}

      {/* E-3: re-request against the new version */}
      {reRequest && (
        <ReRequestModal
          document={reRequest.document}
          cancelled={reRequest.cancelled}
          onClose={() => setReRequest(null)}
          onRequested={() => { setReRequest(null); loadDocuments(); toast('Signatures re-requested against the new version'); }}
        />
      )}

      {/* Version History Modal */}
      {historyDoc && (
        <VersionHistoryModal
          document={historyDoc}
          onClose={() => setHistoryDoc(null)}
        />
      )}

      {/* Move Document Modal */}
      {moveDoc && (
        <MoveDocumentModal
          document={moveDoc}
          folders={folders}
          onClose={() => setMoveDoc(null)}
          onMoved={(destination) => {
            setMoveDoc(null);
            loadDocuments();
            toast(`Moved to ${destination.path || destination.name}`);
          }}
        />
      )}

      {/* Rename Folder Modal */}
      {renameFolderTarget && (
        <RenameFolderModal
          folder={renameFolderTarget}
          onClose={() => setRenameFolderTarget(null)}
          onRenamed={async (updated) => {
            setRenameFolderTarget(null);
            setCurrentFolder(updated);
            await refreshFolders(currentProtocol?.id);
          }}
        />
      )}

      {/* Delete Folder Modal */}
      {deleteFolderTarget && (
        <DeleteFolderModal
          folder={deleteFolderTarget}
          onClose={() => setDeleteFolderTarget(null)}
          onDeleted={async () => {
            const parentId = deleteFolderTarget.parent_folder_id;
            setDeleteFolderTarget(null);
            setCurrentFolder(parentId ? folders.find((f) => f.id === parentId) || null : null);
            await refreshFolders(currentProtocol?.id);
            toast('Folder deleted');
          }}
        />
      )}

      {/* Request Signature Modal — signers are validated server-side against
          the DOCUMENT's site, which can differ from the global site selector
          for multi-site users. */}
      {requestSigDoc && currentSite && (
        <RequestSignatureModal
          document={requestSigDoc}
          siteId={requestSigDoc.site_id || (!siteMode ? currentProtocol?.site_id : undefined) || currentSite.id}
          onClose={() => setRequestSigDoc(null)}
          onRequested={() => { setRequestSigDoc(null); loadDocuments(); }}
        />
      )}

      {/* Delete Confirm Modal */}
      {deleteDoc && (
        <DeleteDocumentModal
          document={deleteDoc}
          onClose={() => setDeleteDoc(null)}
          onDeleted={() => { setDeleteDoc(null); loadDocuments(); }}
        />
      )}

      {/* Pin Key Document Modal (§3.2) */}
      {pinDoc && currentProtocol && (
        <PinKeyDocumentModal
          protocolId={currentProtocol.id}
          document={pinDoc}
          onClose={() => setPinDoc(null)}
          onPinned={() => {
            setPinDoc(null);
            setKeyDocRefresh((n) => n + 1);
            loadDocuments();
          }}
        />
      )}
    </div>
  );
}

// Row menu item. disabledReason renders the item inert with the WHY as a
// tooltip (P-10: explain BEFORE the modal, not with a 400 after it).
function MenuItem({ icon, label, onClick, danger = false, disabledReason = null }: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabledReason?: string | null;
}) {
  if (disabledReason) {
    return (
      <button
        type="button"
        disabled
        title={disabledReason}
        className="flex w-full cursor-not-allowed items-center gap-2 px-3 py-1.5 text-left text-sm text-gray-400 dark:text-gray-500"
      >
        {icon}
        {label}
      </button>
    );
  }
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors duration-150 ${
        danger
          ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
          : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/50'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

// Rename Document Modal
function RenameDocumentModal({ document, onClose, onRenamed }: {
  document: Document;
  onClose: () => void;
  onRenamed: () => void;
}) {
  const [name, setName] = useState(document.document_name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || name.trim() === document.document_name) return;
    setSaving(true);
    setError(null);
    try {
      await eregApi.updateDocument(document.id, { document_name: name.trim() });
      onRenamed();
    } catch (err) {
      console.error('Failed to rename document:', err);
      setError('Failed to rename document');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 w-full max-w-md rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Rename Document</h2>
          <button onClick={onClose} className="rounded p-1 transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Close">
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Document Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`w-full ${fieldCls}`}
              autoFocus
            />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim() || name.trim() === document.document_name}
              className={primaryBtnCls}
            >
              {saving ? 'Renaming...' : 'Rename'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Change Document Type Modal — relabel a misfiled upload's type without
// re-uploading. `document_type` is allow-listed on the PATCH route; the change
// is recorded in the audit trail. Signatures, versions, and folder are untouched.
function ChangeDocumentTypeModal({ document, documentTypes, onClose, onChanged }: {
  document: Document;
  documentTypes: DocumentType[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [type, setType] = useState(document.document_type || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the current value selectable even if it's a legacy/removed type not
  // in the configured list (otherwise the select would silently drop it).
  const hasCurrent = documentTypes.some((t) => t.name === document.document_type);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next = type.trim();
    if (!next || next === document.document_type) return;
    setSaving(true);
    setError(null);
    try {
      await eregApi.updateDocument(document.id, { document_type: next });
      onChanged();
    } catch (err: any) {
      console.error('Failed to change document type:', err);
      setError(err?.response?.data?.error || 'Failed to change the document type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 w-full max-w-md rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Change Document Type</h2>
          <button onClick={onClose} className="rounded p-1 transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Close">
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          <div className="rounded-lg bg-gray-50 p-3 text-sm dark:bg-gray-900/40">
            <p className="font-medium text-gray-900 dark:text-gray-100">{document.document_name}</p>
            <p className="text-gray-500 dark:text-gray-400">Current type: {document.document_type || '—'}</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Document Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className={`w-full ${fieldCls}`}
              autoFocus
              required
            >
              <option value="">Select type…</option>
              {!hasCurrent && document.document_type && (
                <option value={document.document_type}>{document.document_type} (current)</option>
              )}
              {documentTypes.map((t) => (
                <option key={t.id} value={t.name}>{t.name}</option>
              ))}
            </select>
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !type.trim() || type.trim() === document.document_type}
              className={primaryBtnCls}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// New Version Modal
function NewVersionModal({ document, onClose, onUploaded }: {
  document: Document;
  onClose: () => void;
  onUploaded: (result: UploadVersionResponse) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [versionNumber, setVersionNumber] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !versionNumber.trim()) {
      setError('Choose a file and enter a version label');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const result = await eregApi.uploadDocumentVersion(document.id, file, versionNumber.trim());
      onUploaded(result);
    } catch (err: any) {
      console.error('Failed to upload version:', err);
      setError(err?.response?.data?.error || 'Failed to upload new version');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 w-full max-w-md rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Upload New Version</h2>
          <button onClick={onClose} className="rounded p-1 transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Close">
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          <div className="rounded-lg bg-gray-50 p-3 text-sm dark:bg-gray-900/40">
            <p className="font-medium text-gray-900 dark:text-gray-100">{document.document_name}</p>
            <p className="text-gray-500 dark:text-gray-400">Current version: v{document.version_number}</p>
          </div>
          {document.signed_at && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              The current version is signed. Signatures stay with v{document.version_number}; the new version starts unsigned.
            </p>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">File</label>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={secondaryBtnCls}
            >
              <FilePlus2 size={14} />
              {file ? file.name : 'Choose file'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])}
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.csv,.txt"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">New Version Label</label>
            <input
              type="text"
              value={versionNumber}
              onChange={(e) => setVersionNumber(e.target.value)}
              placeholder="e.g., 2.0"
              className={`w-full ${fieldCls}`}
            />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || !file || !versionNumber.trim()}
              className={primaryBtnCls}
            >
              {uploading ? 'Uploading...' : 'Upload Version'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Request Signature Modal
function RequestSignatureModal({ document, siteId, onClose, onRequested }: {
  document: Document;
  siteId: string;
  onClose: () => void;
  onRequested: () => void;
}) {
  const [signers, setSigners] = useState<User[]>([]);
  const [meanings, setMeanings] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [meaning, setMeaning] = useState('Approved');
  const [message, setMessage] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      eregApi.getEligibleSigners(siteId),
      eregApi.getSignatureMeanings().catch(() => ['Approved', 'Reviewed', 'Acknowledged']),
    ])
      .then(([users, meaningList]) => {
        setSigners(users);
        setMeanings(meaningList);
      })
      .catch((err) => {
        console.error('Failed to load signers:', err);
        setError('Failed to load the signer list');
      })
      .finally(() => setLoading(false));
  }, [siteId]);

  const toggleSigner = (id: string) => {
    setSelected(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selected.length === 0) {
      setError('Select at least one signer');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await eregApi.requestSignature({
        document_id: document.id,
        signers: selected.map((userId, index) => ({
          user_id: userId,
          signature_meaning: meaning as any,
          order_number: index + 1,
        })),
        message: message || undefined,
        due_date: dueDate || undefined,
      });
      onRequested();
    } catch (err: any) {
      console.error('Failed to request signature:', err);
      setError(err.response?.data?.error || 'Failed to request signature');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Request Signature</h2>
          <button onClick={onClose} className="rounded p-1 transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Close">
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 space-y-4 overflow-y-auto p-4">
          <div className="rounded-lg bg-gray-50 p-3 text-sm dark:bg-gray-900/40">
            <p className="font-medium text-gray-900 dark:text-gray-100">{document.document_name}</p>
            <p className="text-gray-500 dark:text-gray-400">v{document.version_number} — signatures apply to this version</p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Signers *</label>
            {loading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading signers...</p>
            ) : (
              <div className="max-h-44 overflow-y-auto rounded-lg border border-gray-200 p-2 dark:border-gray-700">
                {signers.map((user) => (
                  <label key={user.id} className="flex cursor-pointer items-center gap-2 rounded p-1.5 transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <input
                      type="checkbox"
                      checked={selected.includes(user.id)}
                      onChange={() => toggleSigner(user.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                    />
                    <span className="text-sm text-gray-900 dark:text-gray-100">{user.full_name}</span>
                    <span className="truncate text-xs text-gray-400 dark:text-gray-500">{user.email}</span>
                  </label>
                ))}
                {signers.length === 0 && (
                  <p className="p-2 text-sm text-gray-500 dark:text-gray-400">No users with access to this site</p>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Meaning of Signature</label>
            <select value={meaning} onChange={(e) => setMeaning(e.target.value)} className={`w-full ${fieldCls}`}>
              {meanings.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Due Date (optional)</label>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={`w-full ${fieldCls}`} />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Message (optional)</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} className={`w-full ${fieldCls}`} />
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || selected.length === 0}
              className={primaryBtnCls}
            >
              <PenTool size={14} />
              {submitting ? 'Sending...' : `Request (${selected.length})`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Delete Document Confirm Modal
function DeleteDocumentModal({ document, onClose, onDeleted }: {
  document: Document;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await eregApi.deleteDocument(document.id);
      onDeleted();
    } catch (err: any) {
      console.error('Failed to delete document:', err);
      // Slot guard (§3.2): the route refuses deleting a pinned key document.
      setError(err?.response?.data?.code === 'EREG_KEY_DOCUMENT_IN_USE'
        ? err.response.data.error
        : 'Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Delete Document</h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Delete <span className="font-medium text-gray-900 dark:text-gray-100">{document.document_name}</span>?
          The document is moved to the deleted list and can be restored later; the action is recorded in the audit trail.
        </p>
        {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="mt-4 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className={dangerBtnCls}
          >
            <Trash2 size={14} />
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Create Folder Modal Component
function CreateFolderModal({
  parentFolder,
  onClose,
  onSubmit,
}: {
  parentFolder: Folder | null;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(name.trim());
    } catch (err: any) {
      setError(err?.message || 'Failed to create folder');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 dark:bg-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Create New Folder</h2>
          <button
            onClick={onClose}
            className="p-1 rounded transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {parentFolder && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Creating subfolder in: <span className="font-medium">{parentFolder.name}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Folder Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`w-full ${fieldCls}`}
              placeholder="e.g., Regulatory Submissions"
              autoFocus
            />
          </div>

          {error && (
            <p className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
              <AlertCircle size={14} />
              {error}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-150 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || submitting}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            >
              {submitting ? 'Creating...' : 'Create Folder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Apply Template Modal Component
function ApplyTemplateModal({
  templates,
  loading,
  onClose,
  onApply,
}: {
  templates: FolderTemplate[];
  loading: boolean;
  onClose: () => void;
  onApply: (templateId: string) => Promise<void>;
}) {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApply = async () => {
    if (!selectedTemplate) return;

    setApplying(true);
    setError(null);
    try {
      await onApply(selectedTemplate);
    } catch (err: any) {
      setError(err?.message || 'Failed to apply template');
    } finally {
      setApplying(false);
    }
  };

  const selectedTemplateData = templates.find(t => t.id === selectedTemplate);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col dark:bg-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Apply Folder Template</h2>
          <button
            onClick={onClose}
            className="p-1 rounded transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
              <p className="mt-2 text-gray-500 dark:text-gray-400">Loading templates...</p>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <FolderIcon size={40} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
              <p>No folder templates available</p>
              <p className="text-sm mt-1">Contact your administrator to create templates</p>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-colors duration-150 ${
                    selectedTemplate === template.id
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="font-medium text-gray-900 dark:text-gray-100">{template.name}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {template.structure.length} top-level folders
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Template Preview */}
          {selectedTemplateData && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg dark:bg-gray-900/40">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Template Structure</h4>
              <div className="text-sm">
                <FolderStructurePreview items={selectedTemplateData.structure} />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-700">
          {error && (
            <p className="mr-auto flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
              <AlertCircle size={14} />
              {error}
            </p>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-150 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!selectedTemplate || applying}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
          >
            {applying ? 'Applying...' : 'Apply Template'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Version History Modal — the document's full version chain, with each
// historical version's bytes one click away (audited content access).
function VersionHistoryModal({ document, onClose }: {
  document: Document;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<DocumentVersionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    eregApi.getDocumentVersions(document.id)
      .then((data) => { if (!cancelled) setDetail(data); })
      .catch((err) => {
        console.error('Failed to load version history:', err);
        if (!cancelled) setError(err?.response?.data?.error || 'Failed to load the version history');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [document.id]);

  const openVersion = async (versionId: string) => {
    // Popup-blocker-safe: open synchronously, then point at the presigned URL.
    const win = window.open('about:blank', '_blank');
    if (win) win.opener = null;
    setOpeningId(versionId);
    try {
      const { url } = await eregApi.getVersionViewUrl(document.id, versionId);
      if (win) {
        win.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener');
      }
    } catch (err: any) {
      if (win) win.close();
      console.error('Failed to open version:', err);
      setError(err?.response?.data?.error || 'Failed to open this version');
    } finally {
      setOpeningId(null);
    }
  };

  const signedVersions = new Set((document.signatures || []).map((s) => s.version_number));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Version History</h2>
            <p className="mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400">{document.document_name}</p>
          </div>
          <button onClick={onClose} className="rounded p-1 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700" aria-label="Close">
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
            </div>
          ) : !detail || detail.versions.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">No version records for this document.</p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {detail.versions.map((version) => {
                const isCurrent = version.id === detail.current_version_id;
                return (
                  <div key={version.id} className="flex items-center gap-3 py-2.5">
                    <History size={16} className="flex-shrink-0 text-gray-400 dark:text-gray-500" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">v{version.version_number}</span>
                        {isCurrent ? (
                          <span className="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800 dark:bg-primary-900/40 dark:text-primary-300">Current</span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700/60 dark:text-gray-300">Superseded</span>
                        )}
                        {signedVersions.has(version.version_number) && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300">
                            <PenTool size={10} />
                            Signed
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                        Uploaded {new Date(version.created_at).toLocaleString()}
                        {version.superseded_at ? ` · superseded ${new Date(version.superseded_at).toLocaleString()}` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => openVersion(version.id)}
                      disabled={openingId !== null}
                      className={secondaryBtnCls}
                    >
                      {openingId === version.id ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                      Open
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>
      </div>
    </div>
  );
}

// Move Document Modal — the misfiled-document fix (was delete-and-re-upload,
// which severed the version chain). PATCH folder_id is allow-listed server-side.
function MoveDocumentModal({ document, folders, onClose, onMoved }: {
  document: Document;
  folders: Folder[];
  onClose: () => void;
  onMoved: (destination: Folder) => void;
}) {
  const [targetId, setTargetId] = useState('');
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = folders.filter((f) => f.id !== document.folder_id);

  const handleMove = async (e: React.FormEvent) => {
    e.preventDefault();
    const destination = options.find((f) => f.id === targetId);
    if (!destination) return;
    setMoving(true);
    setError(null);
    try {
      await eregApi.updateDocument(document.id, { folder_id: destination.id });
      onMoved(destination);
    } catch (err: any) {
      console.error('Failed to move document:', err);
      setError(err?.response?.data?.error || 'Failed to move the document');
    } finally {
      setMoving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 w-full max-w-md rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Move Document</h2>
          <button onClick={onClose} className="rounded p-1 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700" aria-label="Close">
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>
        <form onSubmit={handleMove} className="space-y-4 p-4">
          <div className="rounded-lg bg-gray-50 p-3 text-sm dark:bg-gray-900/40">
            <p className="font-medium text-gray-900 dark:text-gray-100">{document.document_name}</p>
            <p className="text-gray-500 dark:text-gray-400">Versions, signatures, and the audit trail move with it.</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Destination folder</label>
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className={`w-full ${fieldCls}`}
              required
            >
              <option value="">Select folder…</option>
              {options.map((folder) => (
                <option key={folder.id} value={folder.id}>{folder.path || folder.name}</option>
              ))}
            </select>
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
              Cancel
            </button>
            <button type="submit" disabled={moving || !targetId} className={primaryBtnCls}>
              {moving ? <Loader2 size={14} className="animate-spin" /> : <FolderInput size={14} />}
              {moving ? 'Moving…' : 'Move'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Rename Folder Modal
function RenameFolderModal({ folder, onClose, onRenamed }: {
  folder: Folder;
  onClose: () => void;
  onRenamed: (updated: Folder) => void;
}) {
  const [name, setName] = useState(folder.name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || name.trim() === folder.name) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await eregApi.updateFolder(folder.id, { name: name.trim() });
      onRenamed(updated);
    } catch (err: any) {
      console.error('Failed to rename folder:', err);
      setError(err?.response?.data?.error || 'Failed to rename the folder');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 w-full max-w-md rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Rename Folder</h2>
          <button onClick={onClose} className="rounded p-1 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700" aria-label="Close">
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Folder Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`w-full ${fieldCls}`}
              autoFocus
            />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
              Cancel
            </button>
            <button type="submit" disabled={saving || !name.trim() || name.trim() === folder.name} className={primaryBtnCls}>
              {saving ? 'Renaming…' : 'Rename'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Delete Folder Modal — the backend refuses non-empty folders with a readable
// 409 (documents / retained documents / subfolders); surface it verbatim.
function DeleteFolderModal({ folder, onClose, onDeleted }: {
  folder: Folder;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await eregApi.deleteFolder(folder.id);
      onDeleted();
    } catch (err: any) {
      console.error('Failed to delete folder:', err);
      setError(err?.response?.data?.error || 'Failed to delete the folder');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Delete Folder</h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Delete <span className="font-medium text-gray-900 dark:text-gray-100">{folder.name}</span>?
          Only empty folders can be deleted; expected-document placeholders from a template are removed with it.
        </p>
        {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="mt-4 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button onClick={handleDelete} disabled={deleting} className={dangerBtnCls}>
            <Trash2 size={14} />
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// E-3 Re-request Modal — the re-version just cancelled an open request; offer
// one-click re-request of the SAME signers against the new version.
function ReRequestModal({ document, cancelled, onClose, onRequested }: {
  document: Document;
  cancelled: CancelledSignatureRequest;
  onClose: () => void;
  onRequested: () => void;
}) {
  const [signers, setSigners] = useState<User[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve names for the cancelled request's signers (best-effort).
  useEffect(() => {
    let cancelledEffect = false;
    if (!document.site_id) return undefined;
    eregApi.getEligibleSigners(document.site_id)
      .then((users) => { if (!cancelledEffect) setSigners(users); })
      .catch(() => {});
    return () => { cancelledEffect = true; };
  }, [document.site_id]);

  const signerLabel = (userId: string) => {
    const user = signers.find((u) => u.id === userId);
    return user ? `${user.full_name} (${user.email})` : 'Site member';
  };

  const handleReRequest = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await eregApi.requestSignature({
        document_id: document.id,
        signers: cancelled.signers.map((s, index) => ({
          user_id: s.user_id,
          signature_meaning: (s.signature_meaning as any) || 'Approved',
          order_number: index + 1,
        })),
        message: cancelled.message || undefined,
      });
      onRequested();
    } catch (err: any) {
      console.error('Failed to re-request signatures:', err);
      setError(err?.response?.data?.error || 'Failed to re-request signatures');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 w-full max-w-md rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Signature request cancelled</h2>
        </div>
        <div className="space-y-4 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            The new version of <span className="font-medium text-gray-900 dark:text-gray-100">{document.document_name}</span> cancelled
            the open signature request — signatures always apply to a specific version. The affected
            signers have been notified.
          </p>
          <div className="rounded-lg bg-gray-50 p-3 text-sm dark:bg-gray-900/40">
            <p className="mb-1 font-medium text-gray-900 dark:text-gray-100">Previous signers</p>
            <ul className="space-y-0.5 text-gray-600 dark:text-gray-400">
              {cancelled.signers.map((s) => (
                <li key={s.user_id} className="flex items-center gap-1.5">
                  {s.was_pending
                    ? <PenTool size={12} className="text-amber-500" />
                    : <CheckCircle size={12} className="text-green-500" />}
                  {signerLabel(s.user_id)}
                  {!s.was_pending && <span className="text-xs text-gray-400">(had signed the previous version)</span>}
                </li>
              ))}
            </ul>
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">
              Not now
            </button>
            <button onClick={handleReRequest} disabled={submitting || cancelled.signers.length === 0} className={primaryBtnCls}>
              <PenTool size={14} />
              {submitting ? 'Requesting…' : `Re-request against v${document.version_number}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Folder Structure Preview Component
function FolderStructurePreview({ items, depth = 0 }: { items: any[]; depth?: number }) {
  return (
    <ul className={depth > 0 ? 'ml-4 border-l border-gray-200 dark:border-gray-700 pl-2' : ''}>
      {items.map((item, index) => (
        <li key={index} className="py-1">
          <div className="flex items-center gap-2">
            <FolderIcon size={14} className="text-gray-400 dark:text-gray-500" />
            <span className="text-gray-700 dark:text-gray-300">{item.name}</span>
          </div>
          {item.children && item.children.length > 0 && (
            <FolderStructurePreview items={item.children} depth={depth + 1} />
          )}
        </li>
      ))}
    </ul>
  );
}
